import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@captain-tasko/shared';
import { ApiError, apiSingle, setCsrfToken } from '../lib/api.js';

interface SessionEnvelope {
  session: Session;
  csrfToken: string;
}

export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiSingle<SessionEnvelope>('/auth/session');
      setSession(data.session);
      setCsrfToken(data.csrfToken);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null);
        setCsrfToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      // Clear local state so the caller (e.g. AppShell) doesn't keep
      // rendering with a stale session for the brief moment before
      // navigation.
      setSession(null);
      setCsrfToken(null);
      // Hard reload to '/'. Without this, useSession's local state in
      // App.tsx still holds the old session (each useSession() call is
      // a separate React state instance — calling logout in AppShell
      // doesn't propagate up). The reload also drops any TanStack
      // Query caches that hold per-org data, so re-login starts clean.
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { session, loading, refresh, logout };
}
